import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { formatDollarAmount } from 'utils/portfolio/formatDollar'

export interface ColumnConfig {
  key: string
  label: string
  className?: string
  hideOnMobile?: boolean
}

export interface EmptyStateConfig {
  title: string
  icon: string // path to icon
  button?: {
    text: string
    href?: string
    onClick?: () => void
    icon?: string // path to button icon
  }
}

interface PortfolioSectionProps {
  title: string
  subtitle?: string | ReactNode
  subtitleTop?: string | ReactNode
  totalValue?: number
  positionCount?: number
  children: ReactNode

  columns?: ColumnConfig[]
  mobileColumns?: ColumnConfig[]
  showTableHeader?: boolean
  gridTemplateColumns?: string

  isLoading?: boolean
  isEmpty?: boolean
  emptyMessage?: string
  emptyState?: EmptyStateConfig
  emptyComponent?: ReactNode

  headerActions?: ReactNode
  mobileHeaderActions?: ReactNode
  footer?: ReactNode

  containerClassName?: string
  headerClassName?: string
  keepMobileContainer?: boolean
  enableHorizontalScroll?: boolean
  subtitleHighlight?: boolean
}

const STYLES = {
  container:
    'bg-[var(--color-portfolio-filters-bg)] rounded-sm border border-[var(--color-kodiak-slider)] shadow-sm overflow-hidden',
  header: 'p-3 pb-4 sm:p-6 sm:pb-8',
  tableHeader: 'bg-[var(--color-kodiak-table-header-bg)] border-y border-[var(--color-kodiak-slider)] py-2 px-6',
  text: {
    title: 'text-base sm:text-xl font-semibold text-kodiak-text-primary',
    subtitle: {
      container: 'text-sm text-[var(--color-portfolio-text-secondary)]',
      text: 'text-xs leading-none py-1.5 px-2.5 mt-2 border rounded-xs border-portfolio-second-border',
    },
    value: 'text-base sm:text-xl font-semibold text-kodiak-text-primary',
  },
} as const

const LoadingState = ({ title }: { title: string }) => (
  <div className="flex items-center justify-center py-12">
    <div className="text-kodiak-text-secondary">Loading {title.toLowerCase()}...</div>
  </div>
)

const EmptyState = ({ message }: { message: string }) => (
  <div className="flex items-center justify-center py-12">
    <div className="text-kodiak-text-secondary">{message}</div>
  </div>
)

const RichEmptyState = ({ config }: { config: EmptyStateConfig }) => {
  const handleClick = () => {
    if (config.button?.onClick) {
      config.button.onClick()
    }
  }

  const buttonContent = (
    <>
      {config.button?.text}
      {config.button?.icon && <img src={config.button.icon} alt="link icon" className="w-4 h-4" />}
    </>
  )

  return (
    <div className="flex flex-col py-4 sm:py-8 gap-3 sm:gap-5 items-center">
      <img src={config.icon} alt="not found" className="w-8 h-8 sm:w-16 sm:h-16" />
      <div className="text-center text-md text-[var(--color-portfolio-text-secondary)]">{config.title}</div>
      {config.button && (
        <>
          {config.button.href ? (
            <Link
              to={config.button.href}
              className="flex gap-3 items-center py-1 px-3 border border-[var(--color-portfolio-second-border)] rounded-sm text-[var(--color-portfolio-text-white)] leading-[20px] sm:leading-6 text-sm sm:text-base cursor-pointer"
            >
              {buttonContent}
            </Link>
          ) : (
            <button
              onClick={handleClick}
              className="flex gap-3 items-center py-1 px-3 border border-[var(--color-portfolio-second-border)] rounded-sm text-[var(--color-portfolio-text-white)] leading-[20px] sm:leading-6 text-sm sm:text-base cursor-pointer"
            >
              {buttonContent}
            </button>
          )}
        </>
      )}
    </div>
  )
}

export const PortfolioSection = ({
  title,
  subtitle,
  subtitleTop,
  totalValue,
  positionCount,
  children,
  columns,
  mobileColumns,
  showTableHeader = true,
  gridTemplateColumns,
  isLoading = false,
  isEmpty = false,
  emptyMessage = 'No items found',
  emptyState,
  emptyComponent,
  headerActions,
  mobileHeaderActions,
  footer,
  containerClassName = '',
  headerClassName = '',
  keepMobileContainer = false,
  enableHorizontalScroll = false,
  subtitleHighlight = false,
}: PortfolioSectionProps) => {
  const hasTableHeader = showTableHeader && columns && columns.length > 0

  const showPositionCount = positionCount !== undefined && title !== 'xKDK Staking'

  if (isLoading) {
    return (
      <div className={`${STYLES.container} ${containerClassName}`}>
        <div className={STYLES.header}>
          <h2 className={STYLES.text.title}>{title}</h2>
        </div>
        <LoadingState title={title} />
      </div>
    )
  }

  if (isEmpty) {
    return (
      <div className={`${STYLES.container} ${containerClassName}`}>
        <div className={STYLES.header}>
          <h2 className={STYLES.text.title}>{title}</h2>
        </div>
        {emptyComponent ||
          (emptyState ? <RichEmptyState config={emptyState} /> : <EmptyState message={emptyMessage} />)}
      </div>
    )
  }

  return (
    <div
      className={`${
        keepMobileContainer
          ? STYLES.container
          : 'sm:bg-[var(--color-portfolio-filters-bg)] sm:rounded-sm sm:border sm:border-[var(--color-kodiak-slider)] sm:shadow-sm sm:overflow-hidden'
      } ${containerClassName}`}
    >
      <div className={`${keepMobileContainer ? STYLES.header : 'p-3 pb-4 sm:p-6 sm:pb-8'} ${headerClassName}`}>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <h2 className={STYLES.text.title}>{title}</h2>
            {(subtitle || subtitleTop || showPositionCount) && (
              <div className={STYLES.text.subtitle.container}>
                {showPositionCount && (
                  <div>
                    {positionCount} position{positionCount !== 1 ? 's' : ''}
                  </div>
                )}
                {subtitleTop && <div>{subtitleTop}</div>}
                {subtitle && <div className={subtitleHighlight ? STYLES.text.subtitle.text : ''}>{subtitle}</div>}
              </div>
            )}
            {mobileHeaderActions}
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            {headerActions}
            {totalValue !== undefined && <div className={STYLES.text.value}>{formatDollarAmount(totalValue)}</div>}
          </div>
        </div>
      </div>

      {hasTableHeader && mobileColumns && mobileColumns.length > 0 && (
        <div
          className={`sm:hidden grid items-center ${STYLES.tableHeader}`}
          style={{
            gridTemplateColumns:
              mobileColumns.length === 3 ? '2fr 1fr 1fr' : `repeat(${mobileColumns.length}, minmax(0, 1fr))`,
          }}
        >
          {mobileColumns.map((column) => (
            <div
              key={column.key}
              className={`text-xs sm:text-sm font-medium text-kodiak-portfolio-table-header-text ${
                column.className || ''
              }`}
            >
              {column.label}
            </div>
          ))}
        </div>
      )}

      {enableHorizontalScroll ? (
        <div className="hidden sm:block overflow-x-auto">
          <div style={{ minWidth: '1278px' }}>
            {hasTableHeader && (
              <div
                className={`grid items-center ${STYLES.tableHeader}`}
                style={{
                  gridTemplateColumns: gridTemplateColumns || `repeat(${columns.length}, 1fr)`,
                  gap: '1rem',
                }}
              >
                {columns.map((column) => (
                  <div
                    key={column.key}
                    className={`text-sm font-medium text-kodiak-text-secondary ${column.className || ''}`}
                  >
                    {column.label}
                  </div>
                ))}
              </div>
            )}
            <div>{children}</div>
          </div>
        </div>
      ) : (
        <div className="hidden sm:block">
          {hasTableHeader && (
            <div
              className={`grid items-center ${STYLES.tableHeader}`}
              style={{
                gridTemplateColumns: gridTemplateColumns || `repeat(${columns.length}, 1fr)`,
                gap: '1rem',
              }}
            >
              {columns.map((column) => (
                <div
                  key={column.key}
                  className={`text-sm font-medium text-kodiak-text-secondary ${column.className || ''}`}
                >
                  {column.label}
                </div>
              ))}
            </div>
          )}
          <div>{children}</div>
        </div>
      )}

      <div className="sm:hidden">{children}</div>

      {footer}
    </div>
  )
}
